/**
 * Совместимость пары в фирменном знаке «Движа»: три линии скорости из логотипа плюс число.
 *
 * Это оценка ПАРЫ, а не человека (BS §11.4), поэтому подпись говорит «совпадение», а число
 * стоит рядом с полосой, а не вместо неё: линии читаются мгновенно, число — при желании.
 * Нажатие открывает «Почему вы сойдётесь».
 */
import type { Vibe } from '@shared/api/types'
import { Icon, cx } from '@shared/ui'

import styles from './CompatMeter.module.css'
import { BAND_LABEL, bandOf } from './vibe'

/** Сколько линий горит в каждой полосе. */
const BAND_LINES: Record<string, number> = { spark: 3, strong: 3, good: 2, new: 1 }

interface CompatMeterProps {
  vibe: Vibe
  /** Поверх фотографии — стеклянная подложка. */
  onPhoto?: boolean
  size?: 'md' | 'lg'
  onClick?: () => void
  tabIndex?: number
  className?: string
}

export function CompatMeter({
  vibe,
  onPhoto = false,
  size = 'md',
  onClick,
  tabIndex,
  className,
}: CompatMeterProps): React.ReactElement {
  const band = bandOf(vibe)
  const lines = BAND_LINES[band] ?? 1
  const label = `Совпадение ${vibe.score} процентов — ${BAND_LABEL[band]}`

  const content = (
    <>
      <span className={styles['lines']} aria-hidden>
        {[0, 1, 2].map((i) => (
          <i key={i} className={cx(styles['line'], i < lines && styles['lineOn'])} />
        ))}
      </span>
      <span className={styles['value']}>{vibe.score}</span>
      <span className={styles['band']}>{BAND_LABEL[band]}</span>
      {onClick ? (
        <span className={styles['chevron']} aria-hidden>
          <Icon name="chevron-right" size={size === 'lg' ? 18 : 15} />
        </span>
      ) : null}
    </>
  )

  const classes = cx(
    styles['meter'],
    styles[`band-${band}`],
    size === 'lg' && styles['lg'],
    onPhoto && styles['onPhoto'],
    onClick && styles['interactive'],
    className,
  )

  if (onClick) {
    return (
      <button
        type="button"
        className={classes}
        onClick={onClick}
        tabIndex={tabIndex}
        aria-label={`${label}. Почему вы сойдётесь`}
      >
        {content}
      </button>
    )
  }

  return (
    <span className={classes} role="img" aria-label={label}>
      {content}
    </span>
  )
}
